import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Award, Loader2, Wallet } from 'lucide-react'
import type { PageMeta } from '../../../lib/permissions/pageMeta'
import { PageHeader } from '../../../components/ui/PageHeader'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { SearchInput } from '../../../components/ui/SearchInput'
import { useCurrency } from '../../../hooks/useCurrency'
import { usePermissions } from '../../../hooks/usePermissions'
import { useAuthStore } from '../../../store/authStore'
import { supabase } from '../../../lib/supabase'
import { getPaymentAccounts } from '../../../lib/getPaymentAccounts'
import {
  fetchIncentivePayables,
  settleIncentivePayable,
  type IncentivePayableRow,
} from '../../../lib/incentives'

export const pageMeta: PageMeta = {
  key: 'trading.incentive_payables',
  label: 'Incentive Payables',
  module: 'Trading',
  section: 'Incentive Payables',
  description: 'Settle outstanding sales-rep incentive balances',
  defaultPermissions: {
    owner: 'edit', admin: 'edit', manager: 'edit',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

export default function PayablesPage() {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const { format } = useCurrency()
  const { can } = usePermissions()
  const canSettle = can('incentives.settle_payable')

  const [search, setSearch] = useState('')
  const [payRep, setPayRep] = useState<IncentivePayableRow | null>(null)

  const payablesQuery = useQuery({
    queryKey: ['incentive-payables', tid],
    enabled: !!tid,
    queryFn: () => fetchIncentivePayables(tid),
    refetchOnWindowFocus: false,
  })

  const result = payablesQuery.data
  const allRows = useMemo(() => result?.rows ?? [], [result])
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return term ? allRows.filter(r => r.repName.toLowerCase().includes(term)) : allRows
  }, [allRows, search])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Incentive Payables"
        subtitle="Settle outstanding sales-rep incentive balances"
      />

      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
        <span className="text-sm text-gray-500">
          Total outstanding: <strong className="text-gray-900">{format(result?.totalOutstanding ?? 0)}</strong>
        </span>
        <span className="text-sm text-gray-500">
          Reps with balance: <strong className="text-gray-900">{result?.repCount ?? 0}</strong>
        </span>
      </div>

      {allRows.length > 0 && (
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          placeholder="Search representative…"
        />
      )}

      {payablesQuery.isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : allRows.length === 0 ? (
        <EmptyState
          icon={<Award className="h-8 w-8" />}
          title="No outstanding incentive payables"
          description="All caught up! Settled incentives and voided accruals won't appear here."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Award className="h-8 w-8" />}
          title="No matching representatives"
          description="No rep with an outstanding balance matches your search."
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-3">Representative</th>
                  <th className="px-4 py-3 text-right">Total Accrued</th>
                  <th className="px-4 py-3 text-right">Total Settled</th>
                  <th className="px-4 py-3 text-right">Outstanding</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.repId} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.repName}</td>
                    <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{format(r.totalAccrued)}</td>
                    <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{format(r.totalSettled)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{format(r.outstanding)}</td>
                    <td className="px-4 py-3 text-right">
                      {canSettle ? (
                        <button type="button" onClick={() => setPayRep(r)} className="btn-primary py-1.5">
                          Pay
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PayModal rep={payRep} tenantId={tid} onClose={() => setPayRep(null)} />
    </div>
  )
}

function PayModal({
  rep, tenantId, onClose,
}: {
  rep: IncentivePayableRow | null
  tenantId: string
  onClose: () => void
}) {
  const { format } = useCurrency()
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const open = rep !== null

  const [amount, setAmount] = useState('')
  const [accountId, setAccountId] = useState('')
  const [note, setNote] = useState('')

  const { data: accounts = [] } = useQuery({
    queryKey: ['payment-accounts', tenantId],
    enabled: open && !!tenantId,
    queryFn: () => getPaymentAccounts(supabase, tenantId),
  })

  function reset() {
    setAmount('')
    setAccountId('')
    setNote('')
  }

  const outstanding = rep?.outstanding ?? 0
  const parsed = parseFloat(amount)
  const amtValid = Number.isFinite(parsed) && parsed > 0 && parsed <= outstanding + 0.005

  const mutation = useMutation({
    mutationFn: () => settleIncentivePayable({
      tenantId,
      representativeId: rep!.repId,
      amount: parsed,
      paymentAccountId: accountId,
      settledBy: user?.id ?? null,
      note: note.trim() || null,
    }),
    onSuccess: (res) => {
      toast.success(`Settled ${format(res.amount)} for ${res.repName}. Balance now ${format(res.newBalance)}.`)
      qc.invalidateQueries({ queryKey: ['incentive-payables', tenantId] })
      reset(); onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!rep) return null

  return (
    <Modal open={open} onClose={() => { reset(); onClose() }} title={`Settle incentive — ${rep.repName}`} size="sm">
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Outstanding</span>
            <strong className="text-gray-900">{format(outstanding)}</strong>
          </div>
        </div>

        <div>
          <label className="label">Amount *</label>
          <input
            type="number" min="0" step="0.01" className="input"
            value={amount} onChange={e => setAmount(e.target.value)}
            placeholder="0.00"
          />
          {!amount && (
            <button
              type="button"
              className="mt-1 text-xs text-brand-600 hover:underline"
              onClick={() => setAmount(String(outstanding))}
            >
              Pay full {format(outstanding)}
            </button>
          )}
          {amount && parsed > outstanding + 0.005 && (
            <p className="mt-1 text-xs text-red-600">Amount exceeds the outstanding balance.</p>
          )}
        </div>

        <div>
          <label className="label">Pay from account *</label>
          <select className="input" value={accountId} onChange={e => setAccountId(e.target.value)}>
            <option value="">— Select account —</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Note</label>
          <textarea
            value={note} onChange={e => setNote(e.target.value)} rows={2}
            className="input" placeholder="Optional"
          />
        </div>

        {parsed > 0 && accountId && amtValid && (
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-xs space-y-1 font-mono">
            <p className="font-semibold text-gray-600 mb-1 font-sans">Journal Preview</p>
            <div className="flex justify-between">
              <span className="text-blue-700">DR Sales Incentive Payable</span>
              <span className="font-semibold">{format(parsed)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
              <span className="text-green-700">CR {accounts.find(a => a.id === accountId)?.name ?? '—'}</span>
              <span className="font-semibold">{format(parsed)}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={() => { reset(); onClose() }} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !amtValid || !accountId}
          >
            {mutation.isPending
              ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /> Settling…</span>
              : <span className="inline-flex items-center gap-1.5"><Wallet className="h-4 w-4" /> Settle</span>}
          </button>
        </div>
      </div>
    </Modal>
  )
}
